import { useMemo } from 'react';

import type { SkillRecord } from '@nexus/ari-lib/sections/types';
import { useSkills as useSkillsBase, SKILLS_QUERY_KEY } from '@nexus/ari-lib/hooks/use-skills';

import type { UseSkillsApi } from '@nexus/ari-lib/hooks/use-skills';

import { supabaseEdgeFetch } from '../lib/supabase-edge';

export type { SkillRecord };
export { SKILLS_QUERY_KEY };

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

interface SkillsListResponse {
  skills: SkillRecord[];
  user_enabled_skill_ids: string[];
}

// Exported so route loaders can prefetch via queryClient.ensureQueryData.
export async function fetchSkills(): Promise<SkillsListResponse> {
  return supabaseEdgeFetch<SkillsListResponse>('skills');
}

async function enableUserSkill(skillId: string) {
  return supabaseEdgeFetch(`skills/me/${skillId}`, { method: 'POST' });
}

async function disableUserSkill(skillId: string) {
  return supabaseEdgeFetch(`skills/me/${skillId}`, { method: 'DELETE' });
}

async function createSkill(data: { name: string; description?: string; instructions?: string }): Promise<SkillRecord> {
  // POST /skills returns `{ skill }` (see supabase/functions/root/routes/skills/index.ts:317).
  // Unwrap so the lib's createMutation.onSuccess gets a SkillRecord, not a
  // wrapper — otherwise the cache rows become `{ skill: SkillRecord }` and
  // downstream `created.id` for the immediate enableSkill is undefined.
  const res = await supabaseEdgeFetch<{ skill: SkillRecord }>('skills', {
    method: 'POST',
    body: data,
  });
  return res.skill;
}

async function updateSkill(
  skillId: string,
  data: { name?: string; description?: string; instructions?: string }
): Promise<SkillRecord> {
  // PATCH /skills/:id returns `{ skill }` — unwrap like createSkill above.
  const res = await supabaseEdgeFetch<{ skill: SkillRecord }>(`skills/${skillId}`, { method: 'PATCH', body: data });
  return res.skill;
}

async function deleteSkill(skillId: string): Promise<void> {
  await supabaseEdgeFetch(`skills/${skillId}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const api: UseSkillsApi = {
  fetchSkills,
  enableUserSkill,
  disableUserSkill,
  createSkill,
  updateSkill,
  deleteSkill,
};

export function useSkills() {
  return useSkillsBase(useMemo(() => api, []));
}
